
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { LinkConfiguration } from "@/types/block-configurations";

interface LinkControlsProps {
  value?: LinkConfiguration;
  onChange: (value: LinkConfiguration | undefined) => void;
}

/**
 * LinkControls Component
 * 
 * Controls for optional link configuration.
 * Includes enable/disable toggle, URL input, and new tab checkbox.
 * 
 * @param value - Current link configuration (can be undefined)
 * @param onChange - Callback when link configuration changes
 */
export function LinkControls({ value, onChange }: LinkControlsProps) {
  const enabled = value?.enabled || false;

  const toggleEnabled = () => {
    if (enabled) {
      onChange(undefined);
    } else {
      onChange({
        enabled: true,
        url: '',
        openInNewTab: true,
        title: undefined,
      });
    }
  };

  const updateField = <K extends keyof LinkConfiguration>(
    field: K,
    newValue: LinkConfiguration[K]
  ) => {
    if (!value) return;
    onChange({ ...value, [field]: newValue });
  };

  return (
    <div className="space-y-2.5">
      {/* Enable/Disable Link */}
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">Enable Link</Label>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={toggleEnabled}
          className={`
            relative inline-flex h-6 w-11 items-center rounded-full transition-colors
            ${enabled ? 'bg-primary' : 'bg-muted'}
          `}
        >
          <span
            className={`
              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
              ${enabled ? 'translate-x-6' : 'translate-x-1'}
            `}
          />
        </button>
      </div>

      {/* Link Configuration (only show when enabled) */}
      {enabled && value && (
        <>
          {/* URL */}
          <div>
            <Label className="text-xs font-medium mb-1 block">
              URL <span className="text-destructive">*</span>
            </Label>
            <Input
              type="url"
              value={value.url || ''}
              onChange={(e) => updateField('url', e.target.value)}
              placeholder="https://example.com"
              required
            />
          </div>

          {/* Open in New Tab */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="link-new-tab"
              checked={value.openInNewTab}
              onChange={(e) => updateField('openInNewTab', e.target.checked)}
              className="w-4 h-4 rounded border-input"
            />
            <Label htmlFor="link-new-tab" className="text-xs font-medium cursor-pointer">
              Open in new tab
            </Label>
          </div>

          {/* Title (optional) */}
          <div>
            <Label className="text-xs font-medium mb-1 block">
              Title
              <span className="text-muted-foreground ml-1">(optional)</span>
            </Label>
            <Input
              type="text"
              value={value.title || ''}
              onChange={(e) => updateField('title', e.target.value || undefined)}
              placeholder="Link title for accessibility"
              maxLength={500}
            />
          </div>
        </>
      )}
    </div>
  );
}
